import {
  BLOCK_RULE_BAN_DEFAULT_SECONDS,
  BlockRule,
  HttpMethod,
  PathMatchKind,
  type BlockRuleInput,
  type BlockRulePreview,
  type ConfigScopeRef,
} from '@bartizo/shared';
import { useMutation } from '@tanstack/react-query';
import { useId, useState, type ReactNode, type SyntheticEvent } from 'react';
import { previewBlockRule } from '../api/block-rules.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Dialog } from '../ui/Dialog.js';
import { BAN_LENGTHS, PRESETS, slugOf, type Draft } from './block-rule-text.js';
import { SelectField, TextField } from '../ui/Field.js';
import { TriggerFields } from './TriggerFields.js';

/**
 * Making or changing one block rule (knowledge doc section 12, "Block rules";
 * P11.5). Everything is a structured field — paths are literals, chosen as an
 * exact path, a start or an ending — and the shared schema checks the whole rule
 * before it is sent, so what the API refuses is what this form says.
 */

const EMPTY: Draft = {
  kind: 'request',
  id: '',
  name: '',
  site: '',
  enabled: true,
  mode: 'enforce',
  paths: [{ kind: 'exact', value: '' }],
  methods: [],
  response: 'forbidden',
  statuses: '',
  count: '20',
  within: '60',
  ban: String(BLOCK_RULE_BAN_DEFAULT_SECONDS),
  scenarios: '',
  triggerResponse: 'ban',
};

const draftOf = (rule: BlockRule): Draft => ({
  ...EMPTY,
  kind: rule.kind,
  id: rule.id,
  name: rule.name,
  site: rule.site ?? '',
  enabled: rule.enabled,
  mode: rule.mode,
  ...(rule.kind === 'trigger'
    ? {}
    : { paths: rule.paths.map((path) => ({ ...path })), methods: [...rule.methods] }),
  ...(rule.kind === 'trigger'
    ? {
        scenarios: rule.scenarios.join('\n'),
        triggerResponse: rule.response,
        ban: String(rule.ban_seconds),
      }
    : rule.kind === 'request'
      ? { response: rule.response }
      : {
          statuses: rule.statuses.join(', '),
          count: String(rule.count),
          within: String(rule.within_seconds),
          ban: String(rule.ban_seconds),
        }),
});

function ruleOf(draft: Draft): BlockRuleInput {
  if (draft.kind === 'trigger') {
    return {
      id: draft.id,
      name: draft.name.trim(),
      enabled: draft.enabled,
      mode: draft.mode,
      kind: 'trigger',
      scenarios: draft.scenarios.split(/[\s,]+/).filter((each) => each !== ''),
      response: draft.triggerResponse,
      ban_seconds: Number(draft.ban),
    };
  }
  const common = {
    id: draft.id,
    name: draft.name.trim(),
    enabled: draft.enabled,
    mode: draft.mode,
    paths: draft.paths
      .filter((path) => path.value.trim() !== '')
      .map((path) => ({
        kind: path.kind,
        value: path.value.trim(),
      })),
    methods: draft.methods,
    ...(draft.site.trim() === '' ? {} : { site: draft.site.trim().toLowerCase() }),
  };
  if (draft.kind === 'request') return { ...common, kind: 'request', response: draft.response };
  return {
    ...common,
    kind: 'behaviour',
    statuses: draft.statuses
      .split(/[\s,]+/)
      .filter((each) => each !== '')
      .map(Number),
    count: Number(draft.count),
    within_seconds: Number(draft.within),
    ban_seconds: Number(draft.ban),
  };
}

const PATH_KIND_LABEL: Record<PathMatchKind, string> = {
  exact: 'is exactly',
  prefix: 'starts with',
  suffix: 'ends with',
};

export interface BlockRuleDialogProps {
  scope: ConfigScopeRef;
  /**
   * `new`: a rule from nothing or a preset. `edit`: this rule, in place. `copy`: a
   * copy of this rule for one site, which replaces it there — for `site` when
   * given, or for a site typed here.
   */
  mode: 'new' | 'edit' | 'copy';
  rule?: BlockRule;
  site?: string;
  /** Hosts seen in traffic, offered for the site field. */
  hosts?: readonly string[];
  /** A new rule started from the Traffic page (P11.7): a path seen there, and its site. */
  prefill?:
    | { path?: string | undefined; site?: string | undefined; mode?: 'watch' | undefined }
    | undefined;
  busy: boolean;
  error: string | null;
  onSave: (rule: BlockRule) => void;
  onCancel: () => void;
}

export function BlockRuleDialog({
  scope,
  mode,
  rule,
  site,
  hosts = [],
  prefill,
  busy,
  error,
  onSave,
  onCancel,
}: BlockRuleDialogProps): ReactNode {
  const editing = mode === 'edit';
  const copying = mode === 'copy';
  // A rule's id, kind and (but for a copy without one) site are what it is.
  const fixed = editing || copying;
  const siteFixed = editing || site !== undefined;
  const [draft, setDraft] = useState<Draft>(() =>
    rule === undefined
      ? {
          ...EMPTY,
          ...(prefill?.path === undefined
            ? {}
            : { paths: [{ kind: 'exact' as const, value: prefill.path }] }),
          ...(prefill?.site === undefined ? {} : { site: prefill.site }),
          ...(prefill?.mode === undefined ? {} : { mode: prefill.mode }),
        }
      : { ...draftOf(rule), ...(site === undefined ? {} : { site }) },
  );
  const [problem, setProblem] = useState<string | null>(null);
  const hostList = useId();
  const formId = useId();
  const set = (patch: Partial<Draft>): void => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const preview = useMutation<BlockRulePreview, Error, BlockRule>({
    mutationFn: (parsed) => previewBlockRule(scope, parsed),
  });

  const parse = (): BlockRule | undefined => {
    const parsed = BlockRule.safeParse(ruleOf(draft));
    if (parsed.success) {
      setProblem(null);
      return parsed.data;
    }
    const issue = parsed.error.issues[0];
    setProblem(
      issue === undefined
        ? 'Check the rule.'
        : `${issue.path.length === 0 ? '' : `${issue.path.join('.')}: `}${issue.message}`,
    );
    return undefined;
  };

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    const parsed = parse();
    if (parsed !== undefined) onSave(parsed);
  };

  const title = editing
    ? `Edit ${rule?.name ?? 'rule'}`
    : copying
      ? `Change ${rule?.name ?? 'this rule'} for ${site ?? 'one site'}`
      : 'New rule';

  return (
    <Dialog
      variant="panel"
      eyebrow="Rule"
      title={title}
      description="A request rule refuses single requests by path. A behaviour rule counts requests over time and has CrowdSec ban the address. A CrowdSec trigger makes CrowdSec ban, or challenge, whenever a scenario you name fires."
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" busy={busy} busyLabel="Saving…">
            Save rule
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="stack">
        {mode === 'new' && (
          <fieldset className="fieldset">
            <legend>Start from</legend>
            <div className="row">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  title={preset.hint}
                  onClick={() => {
                    setDraft({ ...EMPTY, ...preset.draft });
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </fieldset>
        )}

        <fieldset className="fieldset">
          <legend>Kind</legend>
          {(['request', 'behaviour', 'trigger'] as const).map((kind) => (
            <label key={kind} className="checkbox">
              <input
                type="radio"
                name="kind"
                checked={draft.kind === kind}
                disabled={fixed}
                onChange={() => {
                  set({ kind });
                }}
              />
              {kind === 'request'
                ? 'Request: refuse each matching request'
                : kind === 'behaviour'
                  ? 'Behaviour: ban an address after repeated matches'
                  : 'CrowdSec trigger: ban (or challenge) when a CrowdSec scenario fires'}
            </label>
          ))}
        </fieldset>

        <TextField
          label="Name"
          maxLength={100}
          value={draft.name}
          onChange={(event) => {
            const name = event.target.value;
            set(fixed ? { name } : { name, id: slugOf(name) });
          }}
        />
        {draft.id !== '' && (
          <p className="muted text-xs">
            Rule id <code>{draft.id}</code>
            {fixed ? '' : ' — a site or a narrower layer changes the rule with the same id'}
          </p>
        )}

        {draft.kind !== 'trigger' && (
          <>
            <TextField
              label="Site"
              hint={
                copying
                  ? 'The site this copy is for; it replaces the rule there.'
                  : "Leave empty for every site. A site's copy replaces the rule of the same id there."
              }
              list={hostList}
              disabled={siteFixed}
              value={draft.site}
              onChange={(event) => {
                set({ site: event.target.value });
              }}
            />
            <datalist id={hostList}>
              {hosts.map((host) => (
                <option key={host} value={host} />
              ))}
            </datalist>
          </>
        )}

        {draft.kind !== 'trigger' && (
          <>
            <fieldset className="fieldset">
              <legend>Paths (ignoring case; any one matches)</legend>
              {draft.paths.map((path, index) => (
                <div className="row" key={index}>
                  <SelectField
                    label={`Path ${String(index + 1)} match`}
                    labelHidden
                    value={path.kind}
                    onChange={(event) => {
                      const paths = [...draft.paths];
                      paths[index] = { ...path, kind: PathMatchKind.parse(event.target.value) };
                      set({ paths });
                    }}
                  >
                    {PathMatchKind.options.map((kind) => (
                      <option key={kind} value={kind}>
                        {PATH_KIND_LABEL[kind]}
                      </option>
                    ))}
                  </SelectField>
                  <TextField
                    label={`Path ${String(index + 1)} value`}
                    labelHidden
                    placeholder={path.kind === 'suffix' ? '.php' : '/.env'}
                    value={path.value}
                    onChange={(event) => {
                      const paths = [...draft.paths];
                      paths[index] = { ...path, value: event.target.value };
                      set({ paths });
                    }}
                  />
                  <Button
                    variant="ghost"
                    onClick={() => {
                      set({ paths: draft.paths.filter((_, at) => at !== index) });
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div className="row">
                <Button
                  variant="ghost"
                  onClick={() => {
                    set({ paths: [...draft.paths, { kind: 'exact', value: '' }] });
                  }}
                >
                  Add a path
                </Button>
              </div>
            </fieldset>

            <fieldset className="fieldset">
              <legend>Methods (none ticked: any)</legend>
              <div className="row">
                {HttpMethod.options.map((method) => (
                  <label key={method} className="checkbox">
                    <input
                      type="checkbox"
                      checked={draft.methods.includes(method)}
                      onChange={(event) => {
                        set({
                          methods: event.target.checked
                            ? [...draft.methods, method]
                            : draft.methods.filter((each) => each !== method),
                        });
                      }}
                    />
                    {method}
                  </label>
                ))}
              </div>
            </fieldset>
          </>
        )}

        {draft.kind === 'trigger' ? (
          <TriggerFields draft={draft} set={set} scope={scope} />
        ) : draft.kind === 'request' ? (
          <fieldset className="fieldset">
            <legend>Answer</legend>
            <label className="checkbox">
              <input
                type="radio"
                name="response"
                checked={draft.response === 'forbidden'}
                onChange={() => {
                  set({ response: 'forbidden' });
                }}
              />
              Refuse with 403 — for people and legitimate bots that may ask by mistake
            </label>
            <label className="checkbox">
              <input
                type="radio"
                name="response"
                checked={draft.response === 'drop'}
                onChange={() => {
                  set({ response: 'drop' });
                }}
              />
              Drop the connection (444) — for malicious traffic. Apache answers 403; behind
              Cloudflare or a load balancer, the proxy shows an error instead
            </label>
          </fieldset>
        ) : (
          <>
            <div className="filters">
              <TextField
                label="Requests"
                type="number"
                min={2}
                max={1000}
                value={draft.count}
                onChange={(event) => {
                  set({ count: event.target.value });
                }}
              />
              <TextField
                label="Within (seconds)"
                type="number"
                min={10}
                max={3600}
                value={draft.within}
                onChange={(event) => {
                  set({ within: event.target.value });
                }}
              />
              <SelectField
                label="Ban for"
                value={draft.ban}
                onChange={(event) => {
                  set({ ban: event.target.value });
                }}
              >
                {BAN_LENGTHS.map((each) => (
                  <option key={each.seconds} value={String(each.seconds)}>
                    {each.label}
                  </option>
                ))}
              </SelectField>
            </div>
            <TextField
              label="Only answers with these statuses"
              hint="For example 404, 403. Empty counts every answer — on a PHP site, keep 404 for .php paths."
              value={draft.statuses}
              onChange={(event) => {
                set({ statuses: event.target.value });
              }}
            />
          </>
        )}

        <fieldset className="fieldset">
          <legend>Mode</legend>
          <label className="checkbox">
            <input
              type="radio"
              name="mode"
              checked={draft.mode === 'enforce'}
              onChange={() => {
                set({ mode: 'enforce' });
              }}
            />
            Enforce
          </label>
          <label className="checkbox">
            <input
              type="radio"
              name="mode"
              checked={draft.mode === 'watch'}
              onChange={() => {
                set({ mode: 'watch' });
              }}
            />
            {draft.kind === 'trigger'
              ? 'Watch only — writes nothing; the preview says what it would have banned'
              : 'Watch only — stop nothing; behaviour rules raise events instead of bans'}
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(event) => {
                set({ enabled: event.target.checked });
              }}
            />
            Switched on{draft.site !== '' ? ' for this site' : ''}
          </label>
        </fieldset>

        <div className="row">
          <Button
            variant="ghost"
            busy={preview.isPending}
            busyLabel="Looking…"
            onClick={() => {
              const parsed = parse();
              if (parsed !== undefined) preview.mutate(parsed);
            }}
          >
            {draft.kind === 'trigger' ? 'What would it have banned?' : 'What would it have caught?'}
          </Button>
        </div>
        {preview.data?.trigger !== undefined && (
          <Alert tone="info">
            Over the last week its scenarios raised {preview.data.trigger.alerts.toLocaleString()}{' '}
            alert{preview.data.trigger.alerts === 1 ? '' : 's'} about{' '}
            {preview.data.trigger.addresses.toLocaleString()} address
            {preview.data.trigger.addresses === 1 ? '' : 'es'}.{' '}
            {preview.data.trigger.alreadyBanned.toLocaleString()} already came with a ban;{' '}
            <strong>{preview.data.trigger.wouldBan.toLocaleString()}</strong> did not, and this rule
            would have banned {preview.data.trigger.wouldBan === 1 ? 'it' : 'them'}
            {preview.data.trigger.examples.length > 0
              ? ` (${preview.data.trigger.examples.slice(0, 5).join(', ')}${preview.data.trigger.examples.length > 5 ? ', …' : ''})`
              : ''}
            .
          </Alert>
        )}
        {preview.data !== undefined && preview.data.trigger === undefined && (
          <Alert tone="info">
            Over the last day, at least {preview.data.requests.toLocaleString()} request
            {preview.data.requests === 1 ? '' : 's'} to the busiest paths
            {preview.data.paths.length > 0
              ? ` (${preview.data.paths
                  .slice(0, 5)
                  .map((each) => each.path)
                  .join(', ')})`
              : ''}{' '}
            matched its paths; {preview.data.status4xx.toLocaleString()} of them were already
            refused.
          </Alert>
        )}
        {preview.isError && <Alert tone="danger">{preview.error.message}</Alert>}

        {problem !== null && <Alert tone="danger">{problem}</Alert>}
        {error !== null && <Alert tone="danger">{error}</Alert>}
      </form>
    </Dialog>
  );
}
